// Image Library admin section.
//
// The server owns the surface/category registry. This panel renders the
// grouped response rather than maintaining a second list of persisted keys.
// Writes are file-only: URL-paste controls intentionally remain confined to
// the legacy compatibility endpoints and are not exposed here.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { ToastBanner } from "../../components/ToastBanner.jsx";
import { emitImageryInvalidation } from "../../services/cacheBus.js";

const API_CATEGORIES = "/api/admin/photos/categories?surface=all";
const API_SURFACE = (surface, category) =>
  `/api/admin/photos/surfaces/${encodeURIComponent(surface)}/categories/${encodeURIComponent(category)}`;

async function apiFetch(url, opts = {}) {
  const isJson = !(opts.body instanceof FormData);
  const res = await fetch(url, {
    credentials: "include",
    headers: isJson ? { "Content-Type": "application/json", ...(opts.headers || {}) } : (opts.headers || {}),
    ...opts,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  if (res.status === 204) return null;
  return res.json();
}

function normalizeRegistryResponse(data) {
  if (Array.isArray(data)) {
    return [{
      surface: "in_focus",
      label: "In Focus cards",
      description: "Category images used behind In Focus cards when a location has no image of its own.",
      categories: data.map((entry) => ({
        ...entry,
        key: entry.key || entry.category,
        surface: "in_focus",
        label: entry.label || entry.category,
      })),
    }];
  }
  return Array.isArray(data?.surfaces) ? data.surfaces : [];
}

function UploadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 16V4M6 10l6-6 6 6" /><path d="M4 20h16" />
    </svg>
  );
}

function SurfaceIcon({ surface }) {
  return (
    <span style={{
      width: 26, height: 26, borderRadius: 8, background: T.blueSoft, color: T.blue,
      display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      {surface === "portfolio_command" ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m12 3 9 5-9 5-9-5 9-5Z" /><path d="m3 13 9 5 9-5" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </span>
  );
}

function PrecedenceBanner() {
  const stepStyle = {
    display: "inline-flex", alignItems: "center", gap: 7, fontSize: T.fsXs,
    fontWeight: T.fwBold, color: T.dark, background: T.bg,
    border: `1px solid ${T.border}`, borderRadius: 999, padding: "6px 11px",
  };
  return (
    <div data-testid="image-library-precedence" style={{
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      background: T.white, border: `1px solid ${T.border}`, borderRadius: 10,
      padding: "12px 14px", marginBottom: 20,
    }}>
      <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.light }}>
        How an image is chosen
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
        <span style={stepStyle}><b style={{ color: T.blue }}>1</b> Location image</span>
        <span style={{ color: T.light }}>→</span>
        <span style={stepStyle}><b style={{ color: T.blue }}>2</b> Category image</span>
        <span style={{ color: T.light }}>→</span>
        <span style={stepStyle}><b style={{ color: T.light }}>–</b> Neutral default</span>
      </div>
    </div>
  );
}

function Uploader({ surface, category, onDone, onCancel }) {
  const inputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = new FormData();
      body.append("photo", file);
      await apiFetch(`${API_SURFACE(surface, category)}/upload`, { method: "POST", body });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div data-testid={`uploader-${surface}-${category}`} style={{
      marginTop: 8, padding: "10px 12px", background: T.bg,
      border: `1px solid ${T.border}`, borderRadius: 7,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          data-testid={`choose-file-${surface}-${category}`}
          disabled={submitting}
          onClick={() => inputRef.current?.click()}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 11px",
            background: T.white, border: `1px solid ${T.blue}`, borderRadius: 6,
            color: T.blue, fontFamily: "inherit", fontSize: T.fsSm, fontWeight: T.fwSemibold,
            cursor: submitting ? "not-allowed" : "pointer",
          }}
        >
          <UploadIcon /> {submitting ? "Uploading…" : "Choose file"}
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleFile} />
        <span style={{ fontSize: T.fsXxs, color: T.mid }}>JPEG, PNG, or WebP · 5 MB max</span>
        <button type="button" onClick={onCancel} style={{
          marginLeft: "auto", border: 0, background: "none", color: T.mid,
          fontFamily: "inherit", fontSize: T.fsSm, cursor: "pointer",
        }}>Cancel</button>
      </div>
      {error && <div style={{ marginTop: 6, fontSize: T.fsXs, color: T.red }}>{error}</div>}
    </div>
  );
}

function ImageSlot({ surface, entry, isAdmin, onRefresh, showToast }) {
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const hasImage = !!entry.url;
  const slotTestId = `image-library-slot-${surface}-${entry.key}`;
  const legacyCategoryTestId = surface === "in_focus" ? `category-row-${entry.key}` : undefined;

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(API_SURFACE(surface, entry.key), { method: "DELETE" });
      showToast(`${entry.label} image removed`);
      // The server has completed the write and audit before returning success.
      // Notify mounted consumers only now; failed removals never invalidate.
      emitImageryInvalidation();
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function done() {
    setUploaderOpen(false);
    showToast(`${entry.label} image updated`);
    // Upload/replace is successful only after apiFetch resolves. The event
    // causes Portfolio Command to re-read immediately; there is no TTL.
    emitImageryInvalidation();
    onRefresh();
  }

  return (
    <div data-testid={slotTestId} data-legacy-testid={legacyCategoryTestId} style={{
      border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.white,
    }}>
      <div style={{
        height: 94, position: "relative", display: "flex", alignItems: "flex-end", padding: "9px 10px",
        background: hasImage ? T.bg : `repeating-linear-gradient(45deg, ${T.bg}, ${T.bg} 10px, ${T.lightGray} 10px, ${T.lightGray} 20px)`,
        backgroundImage: hasImage ? `url(${entry.url})` : undefined,
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        {hasImage && surface === "in_focus" && (
          <span data-testid={`card-preview-${entry.key}`} style={{ display: "none" }} aria-hidden="true" />
        )}
        {hasImage && <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 35%, rgba(0,0,0,.48))" }} />}
        <span style={{
          position: "relative", zIndex: 1, color: hasImage ? T.white : T.light,
          fontSize: T.fsSm, fontWeight: T.fwBold, textShadow: hasImage ? "0 1px 4px rgba(0,0,0,.35)" : undefined,
        }}>{entry.label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "9px 10px" }}>
        <span style={{ fontSize: T.fsXs, color: T.mid }}>{entry.description || (hasImage ? "Assigned" : "No image assigned")}</span>
        {isAdmin && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              data-testid={`${hasImage ? "replace" : "upload"}-btn-${surface}-${entry.key}`}
              onClick={() => setUploaderOpen((open) => !open)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 8px",
                background: hasImage ? T.white : T.blueSoft, color: T.blue,
                border: `1px solid ${hasImage ? T.border : T.blue}`, borderRadius: 6,
                fontFamily: "inherit", fontSize: T.fsXxs, fontWeight: T.fwBold, cursor: "pointer",
              }}
            >{hasImage ? "Replace" : <><UploadIcon /> Upload image</>}</button>
            {hasImage && (
              <button
                type="button"
                data-testid={`remove-btn-${surface}-${entry.key}`}
                disabled={busy}
                onClick={remove}
                style={{
                  padding: "5px 8px", background: T.white, color: T.red,
                  border: `1px solid ${T.border}`, borderRadius: 6,
                  fontFamily: "inherit", fontSize: T.fsXxs, fontWeight: T.fwBold, cursor: busy ? "not-allowed" : "pointer",
                }}
              >{busy ? "Removing…" : "Remove"}</button>
            )}
          </div>
        )}
      </div>
      {uploaderOpen && <Uploader surface={surface} category={entry.key} onDone={done} onCancel={() => setUploaderOpen(false)} />}
      {error && <div style={{ padding: "0 10px 8px", fontSize: T.fsXs, color: T.red }}>{error}</div>}
    </div>
  );
}

function SurfaceGroup({ group, isAdmin, onRefresh, showToast }) {
  return (
    <section data-testid={`image-library-group-${group.surface}`} style={{
      background: T.white, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: "16px 17px 18px", marginBottom: 18,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
        <SurfaceIcon surface={group.surface} />
        <h2 style={{ margin: 0, fontSize: T.fsMd5, fontWeight: T.fwSemibold, color: T.dark }}>{group.label}</h2>
        {group.surface === "portfolio_command" && (
          <span style={{ marginLeft: 2, padding: "3px 7px", borderRadius: 6, background: T.ragGreenBg, color: T.green, fontSize: T.fsXxxs5, fontWeight: T.fwBold, textTransform: "uppercase" }}>New</span>
        )}
      </div>
      <p style={{ margin: "0 0 14px", color: T.mid, fontSize: T.fsXs, lineHeight: 1.5 }}>{group.description}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 13 }}>
        {group.categories.map((entry) => (
          <ImageSlot
            key={`${group.surface}:${entry.key}`}
            surface={group.surface}
            entry={entry}
            isAdmin={isAdmin}
            onRefresh={onRefresh}
            showToast={showToast}
          />
        ))}
      </div>
    </section>
  );
}

export function AdminInsightImagerySection({ currentRole, explainer } = {}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const isAdmin = currentRole === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(API_CATEGORIES);
      setGroups(normalizeRegistryResponse(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function showToast(message) {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  }

  return (
    <div data-testid="admin-image-library" data-legacy-testid="admin-insight-imagery-section">
      <div style={{ fontSize: T.fsLg5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 5 }}>Image Library</div>
      {explainer}
      <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: 16, lineHeight: 1.5 }}>
        One place to manage the images used across app surfaces. Slots are grouped by where they appear and can grow with new panes.
      </div>
      <PrecedenceBanner />
      {loading && <div style={{ color: T.mid, fontSize: T.fsSm, padding: "24px 0" }}>Loading image library…</div>}
      {error && <ErrorBanner error={error} />}
      {!loading && !error && groups.map((group) => (
        <SurfaceGroup key={group.surface} group={group} isAdmin={isAdmin} onRefresh={load} showToast={showToast} />
      ))}
      {toast && <ToastBanner message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}